/**
 * useHostBrowser — anchors a CEF host off-screen browser to a DOM rect.
 *
 * History: this module used to also export `useBrowserWebview`, which drove a
 * real Tauri child webview floating over the DOM (positioned via the
 * `commands_browser` overlay commands + the vendored Linux multiwebview patch).
 * That whole overlay path was deleted in ticket 031 now that CEF host browsers
 * (tickets 027–030) replace it. Only the CEF path remains.
 *
 * Under the CEF host there is no Tauri child webview; instead the host runs a
 * real off-screen Chromium browser per id and COMPOSITES it into the OSR surface
 * at a rect we hand it (028/029). So "positioning" is just: measure the anchor
 * in DEVICE pixels and send `setRect`; "hide a tab" is host state (`hide`), not
 * an offscreen park. Because visibility is authoritative host state, there is no
 * racing re-show poll — we only re-measure on real layout events (plus a light
 * poll purely to catch the FlexLayout ancestor-display:none case, which fires no
 * observer).
 */
import { useCallback, useEffect, useRef } from 'react';
import { isCefHost } from '@/services/backend';
import type { HostBrowserControl, HostBrowserRect } from '@/services/backend/cefBrowser';
import { silently } from '@/services/backend/cefBrowser';
import { logger } from '@/lib/logger';

export interface UseBrowserWebviewArgs {
  /** Stable browser label — typically `browser-<windowLabel>-<projectId>-<tabId>`
   *  so each tab gets exactly one host browser that lives across activations. */
  label: string;
  /** URL to load. Empty string means "don't open the browser yet". */
  url: string;
  /** Ref to the empty positioned div that marks where the browser should
   *  composite. The hook measures this element on every relevant layout event
   *  and uses an IntersectionObserver to detect when FlexLayout hides the
   *  tab (display:none zeroes the rect, IO fires isIntersecting=false). */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** When true, host-hide the browser regardless of layout state. Used to step
   *  out of the way of modals/dialogs that render above the React DOM. */
  forceHidden?: boolean;
  /** Isolated cookie-jar key (the project's Claude identity) so work artifacts
   *  open under the work claude.ai session, personal under personal. Empty/
   *  undefined = the shared default context. */
  context?: string;
}

export interface UseBrowserWebviewApi {
  reload: () => Promise<void>;
  navigate: (url: string) => Promise<void>;
  /** Async because it (would) round-trip to the browser to read window.location. */
  getCurrentUrl: () => Promise<string | null>;
}

const CEF_TAG = 'useHostBrowser';

/** Measure an element's viewport rect in DEVICE pixels (OSR surface space). */
function measureDeviceRect(el: HTMLElement): HostBrowserRect | null {
  // A pane hidden via display:none / visibility:hidden / content-visibility is
  // "not laid out" → treat as no-rect so the caller hides instead of showing.
  if (
    typeof el.checkVisibility === 'function' &&
    !el.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true } as CheckVisibilityOptions)
  ) {
    return null;
  }
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return null;
  const dpr = window.devicePixelRatio || 1;
  return {
    x: Math.round(r.left * dpr),
    y: Math.round(r.top * dpr),
    w: Math.round(r.width * dpr),
    h: Math.round(r.height * dpr),
  };
}

function rectsEqual(a: HostBrowserRect | null, b: HostBrowserRect | null): boolean {
  if (!a || !b) return a === b;
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/** Off-screen parking rect for a not-yet-visible tab's browser: a real viewport
 *  size (so the page lays out normally and the agent gets a real 1280×800
 *  browser) parked far off-canvas. The instance is host-hidden immediately after
 *  create regardless, so position is moot — but negative coords also avoid any
 *  one-frame paint at (0,0) before the hide lands. Resized to the panel rect on
 *  first show. */
const OFFSCREEN_RECT: HostBrowserRect = { x: -32000, y: -32000, w: 1280, h: 800 };

export function useHostBrowser(
  ctl: HostBrowserControl,
  { label, url, anchorRef, forceHidden = false, context }: UseBrowserWebviewArgs,
): UseBrowserWebviewApi {
  const createdRef = useRef(false);
  const visibleRef = useRef(false);
  const lastRectRef = useRef<HostBrowserRect | null>(null);
  const forceHiddenRef = useRef(forceHidden);
  forceHiddenRef.current = forceHidden;
  // Latest url, so the create effect and sync don't fight over stale closures.
  const urlRef = useRef(url);
  urlRef.current = url;
  const contextRef = useRef(context);
  contextRef.current = context;

  const sync = useCallback(() => {
    if (!isCefHost()) return;
    const el = anchorRef.current;
    const rect = forceHiddenRef.current || !el ? null : measureDeviceRect(el);

    // Create the OSR browser as soon as the tab has a url — even if it isn't
    // visible yet. Previously create waited for a visible rect, so a hidden /
    // inactive / agent-just-opened tab had NO host browser, which meant
    // get_browser(label) missed it and the whole agent control channel
    // (eval/click/key/screenshot) no-op'd on it. We park it off-screen and
    // immediately host-hide; it still registers, paints to its own frame
    // buffer, and accepts input — fully drivable before the user ever views it.
    // It composites into the panel only when active (the show/setRect path).
    if (!createdRef.current) {
      if (!urlRef.current) return;
      createdRef.current = true;
      visibleRef.current = !!rect;
      lastRectRef.current = rect;
      const createRect = rect ?? OFFSCREEN_RECT;
      silently(
        ctl
          .create(label, urlRef.current, createRect, contextRef.current)
          .then(() => (rect ? ctl.focus(label, /*exclusive=*/ true) : ctl.hide(label))),
        `${CEF_TAG}.create`,
      );
      return;
    }

    if (!rect) {
      // Became hidden (inactive tab / modal / not laid out). Stop compositing,
      // but the host browser stays alive + drivable by the agent.
      if (visibleRef.current) {
        visibleRef.current = false;
        lastRectRef.current = null;
        silently(ctl.hide(label), `${CEF_TAG}.hide`);
      }
      return;
    }

    // Visible rect available. Re-show if we were hidden, and push the rect if it moved.
    if (!visibleRef.current) {
      visibleRef.current = true;
      lastRectRef.current = rect;
      silently(ctl.show(label).then(() => ctl.setRect(label, rect)), `${CEF_TAG}.show`);
      return;
    }
    if (!rectsEqual(lastRectRef.current, rect)) {
      lastRectRef.current = rect;
      silently(ctl.setRect(label, rect), `${CEF_TAG}.setRect`);
    }
  }, [ctl, label, anchorRef]);

  // Re-sync on forceHidden flips (modal open/close) — park or restore.
  useEffect(() => {
    sync();
  }, [forceHidden, sync]);

  // Navigate an already-created browser when (label,url) changes; if not yet
  // created, sync() handles the initial create-with-url.
  useEffect(() => {
    if (!isCefHost() || !url) return;
    if (createdRef.current) {
      silently(ctl.navigate(label, url), `${CEF_TAG}.navigate(url-change)`);
    } else {
      sync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, url]);

  // Layout sync wiring. The poll exists ONLY for the FlexLayout case where an
  // ancestor flips display:none (no observer fires) — it's a cheap
  // getBoundingClientRect + a setRect/hide only when something changed, and it
  // never re-shows a hidden browser (host visibility is authoritative).
  useEffect(() => {
    if (!isCefHost()) return;
    const el = anchorRef.current;
    if (!el) return;

    sync();

    const ro = new ResizeObserver(sync);
    ro.observe(el);
    // Also observe the parent: FlexLayout resizes the containing panel on a
    // splitter drag, and although the absolute-inset anchor resizes with it,
    // observing the parent too makes the update fire promptly.
    if (el.parentElement) ro.observe(el.parentElement);
    const io = new IntersectionObserver(sync, { threshold: [0, 0.01, 0.5, 1] });
    io.observe(el);
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    const interval = window.setInterval(sync, 250);

    // While the pointer is held (e.g. dragging a FlexLayout splitter) the panel
    // resizes continuously — run sync every animation frame so the composited
    // browser tracks the drag smoothly instead of lagging behind the poll and
    // visibly overlapping the neighbouring panel.
    let rafId = 0;
    let dragging = false;
    const frame = () => {
      if (!dragging) return;
      sync();
      rafId = requestAnimationFrame(frame);
    };
    const onPointerDown = () => {
      if (dragging) return;
      dragging = true;
      rafId = requestAnimationFrame(frame);
    };
    const onPointerUp = () => {
      dragging = false;
      if (rafId) cancelAnimationFrame(rafId);
      sync(); // final settle
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('pointerup', onPointerUp, true);

    return () => {
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('pointerup', onPointerUp, true);
      if (rafId) cancelAnimationFrame(rafId);
      window.clearInterval(interval);
    };
  }, [sync, anchorRef]);

  // On unmount, HIDE (don't close) — the host browser persists across project
  // switches / tab close-reopen. Explicit close is the user's tab-x
  // (BrowserTab.closeTab).
  useEffect(() => {
    return () => {
      if (!isCefHost() || !createdRef.current) return;
      silently(ctl.hide(label), `${CEF_TAG}.hide(unmount)`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    reload: useCallback(async () => {
      // No dedicated reload command on the host; re-navigate to the current url.
      if (urlRef.current) await ctl.navigate(label, urlRef.current).catch(() => { /* noop */ });
    }, [ctl, label]),
    navigate: useCallback(
      async (nextUrl: string) => {
        urlRef.current = nextUrl;
        if (createdRef.current) {
          await ctl.navigate(label, nextUrl).catch((e) => logger.warn(CEF_TAG, 'navigate failed', e));
        } else {
          // Not created yet — let the next sync create it at the right rect.
          sync();
        }
      },
      [ctl, label, sync],
    ),
    // The host doesn't yet expose a "read current url" round-trip (that's a
    // later eval/getText ticket); report null so the URL bar falls back to the
    // tab's tracked url.
    getCurrentUrl: useCallback(async () => null, []),
  };
}
